const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export const fmt = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("en-IN") : "—";

export const pct = (v, digits = 1) =>
  typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(digits)}%` : "—";

export const formatLakh = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  return fmt(n);
};

export const formatCompactK = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return fmt(n);
};

const MGMT_COLORS = ["#2563eb", "#14b8a6", "#ff8a1f", "#ef4444", "#8b5cf6", "#06b6d4"];

export const buildGirlsGaugeStyle = (girlsPct = 0) => {
  const boysPct = clamp(100 - girlsPct, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 55%, transparent 56%), conic-gradient(#2563eb 0 ${boysPct}%, #ec4899 ${boysPct}% 100%)`,
  };
};

export const buildHeroNarrative = (overview = {}) => {
  const peak = overview.peak_enrolment_age ?? "—";
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const dropPct = Math.round((1 - retention) * 100);

  const transitionNote =
    retention >= 0.9
      ? "secondary transition retention is healthy"
      : retention >= 0.8
        ? `secondary transition needs watch with a ${dropPct}% drop signal`
        : `secondary transition is weak with a ${dropPct}% drop from primary`;

  const parityNote =
    gpi >= 1
      ? `gender parity is at ${gpi.toFixed(3)} and above the 1.0 threshold`
      : gpi >= 0.97
        ? `gender parity is near target at ${gpi.toFixed(3)}`
        : `gender parity at ${gpi.toFixed(3)} needs targeted retention support for girls`;

  return `Age ${peak} is the peak enrolment point, ${transitionNote}. ${parityNote}.`;
};

export const buildPrimaryExecutiveAction = (overview = {}) => {
  const dropPct = Math.round((1 - (overview.secondary_retention_index ?? 0)) * 100);
  return `Prioritize block-wise review of girls' retention between ages 14–18 and convert the ${dropPct}% secondary drop signal into school-level action lists.`;
};

export const buildExecutiveKpis = (overview = {}) => {
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const dropPct = Math.round((1 - retention) * 100);

  return [
    {
      label: "Total Enrolment",
      value: fmt(overview.total_enrolment),
      sub: "Gross student enrolment",
      cardClass: "",
    },
    {
      label: "Gender Parity Index",
      value: gpi.toFixed(3),
      sub: gpi < 0.97 ? "Below parity threshold" : "Near parity threshold",
      cardClass: gpi < 0.97 ? "warning" : "",
    },
    {
      label: "Average Per School",
      value: fmt(Math.round(overview.avg_students_per_school ?? 0)),
      sub: `Across ${fmt(overview.total_schools)} schools`,
      cardClass: "",
    },
    {
      label: "Early Age 4–6",
      value: pct(overview.early_age_pct ?? 0),
      sub: `${fmt(overview.early_age_total)} students`,
      cardClass: "warning",
    },
    {
      label: "Peak Enrolment Age",
      value: `${overview.peak_enrolment_age ?? "—"} yrs`,
      sub: `${fmt(overview.peak_enrolment_count)} students`,
      cardClass: "",
    },
    {
      label: "Secondary Retention",
      value: retention.toFixed(2),
      sub: `${dropPct}% drop from primary`,
      cardClass: "danger",
    },
  ];
};

export const buildGenderBars = (overview = {}) => {
  const boys = overview.total_boys ?? 0;
  const girls = overview.total_girls ?? 0;
  const total = boys + girls || 1;
  const boysPct = (boys / total) * 100;
  const girlsPct = (girls / total) * 100;
  return [
    { label: "Boys", value: boys, widthPct: boysPct, barClass: "" },
    { label: "Girls", value: girls, widthPct: girlsPct, barClass: "girls" },
  ];
};

export const buildManagementDonut = (managementData = [], overview = {}) => {
  const total = overview.total_enrolment ?? managementData.reduce((s, m) => s + (m.total_enrolment ?? 0), 0);
  const top = [...managementData]
    .sort((a, b) => (b.total_enrolment ?? 0) - (a.total_enrolment ?? 0))
    .slice(0, 6);

  let cursor = 0;
  const stops = top.map((row, idx) => {
    const share = total > 0 ? ((row.total_enrolment ?? 0) / total) * 100 : 0;
    const start = cursor;
    cursor += share;
    return `${MGMT_COLORS[idx % MGMT_COLORS.length]} ${start}% ${cursor}%`;
  });

  const legend = top.map((row, idx) => {
    const share = total > 0 ? ((row.total_enrolment ?? 0) / total) * 100 : 0;
    const name = row.management_name ?? "—";
    const shortName =
      name.length > 22 ? `${name.slice(0, 20)}…` : name;
    return {
      name: shortName,
      fullName: name,
      pct: Math.round(share),
      color: MGMT_COLORS[idx % MGMT_COLORS.length],
    };
  });

  return {
    style: {
      background: stops.length
        ? `conic-gradient(${stops.join(", ")})`
        : "conic-gradient(#e2e8f0 0 100%)",
    },
    legend,
  };
};

export const buildStageLens = (overview = {}) => {
  const retention = overview.secondary_retention_index ?? 0;
  return [
    { value: pct(overview.early_age_pct ?? 0), label: "Early Age 4–6" },
    { value: `${overview.peak_enrolment_age ?? "—"} yrs`, label: "Peak Age" },
    { value: retention.toFixed(2), label: "Retention Index" },
    { value: `${Math.round((1 - retention) * 100)}%`, label: "Drop Signal" },
  ];
};

export const buildAgeTrendSeries = (ageData = []) => {
  const series = [];
  for (let age = 4; age <= 18; age += 1) {
    const row = ageData.find((d) => parseInt(d.age, 10) === age);
    series.push({ age, total: row?.total ?? 0 });
  }
  return series;
};

export const buildAgeChartModel = (ageData = [], overview = {}) => {
  const series = buildAgeTrendSeries(ageData);
  const max = Math.max(...series.map((s) => s.total), 1);
  const width = 880;
  const height = 360;
  const left = 70;
  const right = 40;
  const top = 40;
  const bottom = 60;
  const chartW = width - left - right;
  const chartH = height - top - bottom;

  const points = series.map((row, idx) => {
    const x = left + (idx / (series.length - 1)) * chartW;
    const y = top + chartH - (row.total / max) * chartH * 0.88;
    return { ...row, x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)} ${height - bottom} L${left} ${height - bottom} Z`;

  const peakAge = overview.peak_enrolment_age ?? series.reduce((best, s) => (s.total > best.total ? s : best), series[0]).age;
  const peakPoint = points.find((p) => p.age === peakAge) ?? points[0];

  const declineStart = points.find((p) => p.age === 16) ?? points[points.length - 3];
  const declineEnd = points[points.length - 1];
  const declinePath = `M${declineStart.x.toFixed(1)} ${declineStart.y.toFixed(1)} C${(declineStart.x + 40).toFixed(1)} ${(declineStart.y + 10).toFixed(1)}, ${(declineEnd.x - 30).toFixed(1)} ${(declineEnd.y + 80).toFixed(1)}, ${declineEnd.x.toFixed(1)} ${declineEnd.y.toFixed(1)}`;

  const yTicks = [0.25, 0.5, 0.75, 1].map((frac) => ({
    y: top + chartH - chartH * frac * 0.88,
    label: formatCompactK(max * frac),
  }));

  return {
    width,
    height,
    left,
    bottom: height - bottom,
    points,
    linePath,
    areaPath,
    peakPoint,
    peakAge,
    declinePath,
    yTicks,
  };
};

export const buildPriorityCards = (overview = {}) => {
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const enrolmentLakh = formatLakh(overview.total_enrolment);

  return [
    {
      id: "scale",
      tag: "Scale",
      tagClass: "blue",
      iconClass: "",
      title: "Monitor the Total Enrolment Base",
      body: `Track block-wise movement against the ${enrolmentLakh} student base and flag schools with sudden age-wise variance.`,
      metric: fmt(overview.total_blocks),
      metricLabel: "blocks covered",
    },
    {
      id: "parity",
      tag: "Parity gap",
      tagClass: "",
      iconClass: "pink",
      title: "Improve Gender Parity",
      body: "Target schools with girls' share below district average and overlay retention, management type and age group.",
      metric: gpi.toFixed(3),
      metricLabel: "GPI",
    },
    {
      id: "retention",
      tag: "Retention risk",
      tagClass: "red",
      iconClass: "orange",
      title: "Act on Secondary Drop-Off",
      body: "Create a school-level watchlist for ages 14–18 and review transition support before the next monthly review.",
      metric: retention.toFixed(2),
      metricLabel: "retention index",
    },
  ];
};

export const buildRiskWatchlist = (overview = {}) => {
  const girlsPct = overview.girls_pct ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const earlyPct = overview.early_age_pct ?? 0;

  return [
    {
      signal: `Girls ${girlsPct}%`,
      why: "Parity is below ideal threshold and requires block-level intervention.",
      owner: "District Education Officer",
      status: "Amber",
      tagClass: "",
    },
    {
      signal: `Age ${overview.peak_enrolment_age ?? "—"} Peak`,
      why: "Highest enrolment concentration should be protected through transition planning.",
      owner: "Block Education Officer",
      status: "Stable",
      tagClass: "green",
    },
    {
      signal: `Retention ${retention.toFixed(2)}`,
      why: "Indicates secondary-age transition risk and possible dropout pressure.",
      owner: "School Heads",
      status: "High Priority",
      tagClass: "red",
    },
    {
      signal: pct(earlyPct),
      why: "Early-age enrolment needs stage-wise tracking to detect under-age/over-age patterns.",
      owner: "Cluster Coordinators",
      status: "Review",
      tagClass: "",
    },
  ];
};

export const buildWorkflowActions = (overview = {}, blockData = []) => {
  const retention = overview.secondary_retention_index ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const lowGpiBlocks = blockData.filter((b) => (b.gender_parity_index ?? b.gpi ?? 1) < 0.9).length;

  return [
    {
      title: "Generate girls' retention heatmap",
      body: `Compare girls' share (GPI ${gpi}) and secondary retention (${(retention * 100).toFixed(1)}%) across blocks and age cohorts.`,
      iconClass: "pink",
    },
    {
      title: "Build Post-16 Transition List",
      body:
        retention < 0.75
          ? `Secondary retention is ${(retention * 100).toFixed(1)}%; identify schools with post-16 drop-off for counselling support.`
          : "Monitor post-16 transition schools even though secondary retention is currently stable.",
      iconClass: "orange",
    },
    {
      title: "Publish Block Review Pack",
      body: `Send a weekly brief covering ${blockData.length} blocks${lowGpiBlocks ? `, including ${lowGpiBlocks} low-GPI blocks` : ""}.`,
      iconClass: "",
    },
  ];
};

const ageRow = (ageData, age) =>
  ageData.find((d) => parseInt(d.age, 10) === age) ?? {
    age,
    boys: 0,
    girls: 0,
    total: 0,
    gpi: 0,
  };

const sumAges = (ageData, ages) =>
  ages.reduce(
    (acc, age) => {
      const row = ageRow(ageData, age);
      acc.boys += row.boys ?? 0;
      acc.girls += row.girls ?? 0;
      acc.total += row.total ?? 0;
      return acc;
    },
    { boys: 0, girls: 0, total: 0 }
  );

export const buildGpiGaugeStyle = (gpi = 0) => {
  const fill = clamp(gpi * 100, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#14b8a6 0 ${fill}%, rgba(20,184,166,.13) ${fill}% 100%)`,
  };
};

export const getGpiStatus = (gpi = 0) => {
  if (gpi >= 0.95) return { label: "Good", tagClass: "" };
  if (gpi >= 0.86) return { label: "Watch", tagClass: "amber" };
  if (gpi >= 0.7) return { label: "Risk", tagClass: "red" };
  return { label: "Critical", tagClass: "red" };
};

export const buildAgeGenderHero = (overview = {}) => {
  const gpi = overview.gender_parity_index ?? 0;
  const girlsPct = overview.girls_pct ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const dropPct = Math.round((1 - retention) * 100);

  const gpiChip =
    gpi >= 0.97 && gpi <= 1.03 ? "Near parity overall" : gpi < 0.97 ? "Girls lag overall" : "Boys lag overall";

  return {
    headline: "Gender Parity Is Close, but Girls Lag Widens After Age 14.",
    narrative: `The dashboard now prioritizes the story: overall participation is healthy at ${girlsPct}%, while the key management question is secondary-age retention and the sharp parity decline at ages 17–18.`,
    chips: [
      { label: gpiChip, className: "green" },
      { label: "Secondary-Age Risk", className: "red" },
      { label: "Ages 4–18 Monitored", className: "" },
    ],
    action:
      "Run a block-wise secondary retention review for girls in ages 14–18 and investigate the age 17–18 parity drop before the next review cycle.",
    gpiLabel: gpi.toFixed(3),
    dropPct,
  };
};

export const buildGenderSplitMetrics = (overview = {}) => {
  const boys = overview.total_boys ?? 0;
  const girls = overview.total_girls ?? 0;
  const total = boys + girls || 1;
  const boysPct = (boys / total) * 100;
  const girlsPct = (girls / total) * 100;
  const gap = Math.abs(boys - girls);

  return {
    totalLabel: formatLakh(overview.total_enrolment),
    boys,
    girls,
    gap,
    boysPct,
    girlsPct,
    boysWidth: boysPct,
    girlsWidth: girlsPct,
  };
};

export const buildAgeGenderKpis = (overview = {}) => {
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const dropPct = Math.round((1 - retention) * 100);
  const gpiSub = gpi < 0.97 ? "Girls lag, near parity" : gpi > 1.03 ? "Boys lag" : "Near parity";

  return [
    {
      label: "Total Enrolment",
      value: fmt(overview.total_enrolment),
      sub: "Students aged 4–18",
      iconClass: "",
      pill: "Base",
    },
    {
      label: "Girls Participation",
      value: pct(overview.girls_pct ?? 0),
      sub: `${fmt(overview.total_girls)} girls`,
      iconClass: "pink",
      pill: "Girls",
    },
    {
      label: "Gender Parity Index",
      value: gpi.toFixed(3),
      sub: gpiSub,
      iconClass: "green",
      pill: "Parity",
    },
    {
      label: "Retention Index",
      value: retention.toFixed(2),
      sub: `${dropPct}% drop-off from primary`,
      iconClass: "orange",
      pill: "Risk",
    },
  ];
};

export const buildStageGenderCards = (ageData = []) => {
  const stages = [
    {
      title: "Early Childhood · 4–6",
      ages: [4, 5, 6],
      metaLeft: (boysPct) => `Boys ${boysPct.toFixed(1)}%`,
      metaRight: (girlsPct) => `Girls ${girlsPct.toFixed(1)}%`,
    },
    {
      title: "Primary · 7–10",
      ages: [7, 8, 9, 10],
      metaLeft: () => "GPI steady",
      metaRight: () => "Peak enrolment",
    },
    {
      title: "Upper Primary · 11–13",
      ages: [11, 12, 13],
      metaLeft: () => "Healthy base",
      metaRight: () => "Watch age 13",
    },
    {
      title: "Secondary · 14–18",
      ages: [14, 15, 16, 17, 18],
      metaLeft: () => "Retention focus",
      metaRight: () => "GPI weakens",
    },
  ];

  return stages.map((stage) => {
    const agg = sumAges(ageData, stage.ages);
    const total = agg.total || 1;
    const boysPct = (agg.boys / total) * 100;
    const girlsPct = (agg.girls / total) * 100;
    return {
      title: stage.title,
      total: fmt(agg.total),
      boysWidth: boysPct,
      girlsWidth: girlsPct,
      metaLeft:
        typeof stage.metaLeft === "function" && stage.ages[0] === 4
          ? stage.metaLeft(boysPct)
          : stage.metaLeft(),
      metaRight:
        typeof stage.metaRight === "function" && stage.ages[0] === 4
          ? stage.metaRight(girlsPct)
          : stage.metaRight(),
    };
  });
};

export const buildComposedAgeGenderChart = (ageData = []) => {
  const series = [];
  for (let age = 4; age <= 18; age += 1) {
    const row = ageRow(ageData, age);
    series.push({
      age,
      boys: row.boys ?? 0,
      girls: row.girls ?? 0,
      gpi: row.gpi ?? 0,
    });
  }

  const maxEnrol = Math.max(...series.map((s) => Math.max(s.boys, s.girls)), 1);
  const yMax = Math.max(Math.ceil(maxEnrol / 30000) * 30000, maxEnrol);
  const chartH = 288;
  const step = 61;
  const startX = 18;
  const barW = 22;
  const gpiMin = 0.4;
  const gpiMax = 1.2;

  const bars = series.map((row, idx) => {
    const x = startX + idx * step;
    const boysH = (row.boys / yMax) * chartH;
    const girlsH = (row.girls / yMax) * chartH;
    const gpiY = chartH - ((row.gpi - gpiMin) / (gpiMax - gpiMin)) * chartH;
    return {
      age: row.age,
      boys: { x, y: chartH - boysH, h: boysH },
      girls: { x: x + 25, y: chartH - girlsH, h: girlsH },
      gpi: { x: x + 11, y: gpiY },
    };
  });

  const gpiPoints = bars.map((b) => `${b.gpi.x},${b.gpi.y.toFixed(1)}`).join(" ");
  const zoneStart = startX + 10 * step - 8;
  const zoneWidth = 5 * step + 40;

  const yTicks = [1, 0.75, 0.5, 0.25, 0].map((frac) => ({
    y: chartH * (1 - frac),
    label: frac === 0 ? "0" : `${Math.round((yMax * frac) / 1000)}K`,
  }));

  const gpiTicks = [1.2, 1.0, 0.8, 0.6, 0.4].map((gpi) => ({
    y: chartH - ((gpi - gpiMin) / (gpiMax - gpiMin)) * chartH,
    label: gpi.toFixed(1),
  }));

  return { bars, gpiPoints, zoneStart, zoneWidth, yTicks, gpiTicks, yMax };
};

export const buildGpiLineChart = (ageData = []) => {
  const series = [];
  for (let age = 4; age <= 18; age += 1) {
    const row = ageRow(ageData, age);
    series.push({ age, gpi: row.gpi ?? 0 });
  }

  const chartW = 520;
  const chartH = 272;
  const gpiMin = 0.5;
  const gpiMax = 1.1;
  const parityY = chartH - ((0.95 - gpiMin) / (gpiMax - gpiMin)) * chartH;

  const points = series.map((row) => {
    const x = ((row.age - 4) / 14) * chartW;
    const y = chartH - ((row.gpi - gpiMin) / (gpiMax - gpiMin)) * chartH;
    return { age: row.age, x, y, gpi: row.gpi };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const labelAges = [4, 6, 8, 10, 12, 14, 16, 17, 18];
  const xLabels = labelAges.map((age) => ({
    age,
    x: ((age - 4) / 14) * chartW,
  }));

  const yTicks = [1.1, 0.95, 0.8, 0.65, 0.5].map((gpi) => ({
    y: chartH - ((gpi - gpiMin) / (gpiMax - gpiMin)) * chartH,
    label: gpi.toFixed(2).replace(/\.?0+$/, gpi === 0.95 ? "0.95" : gpi.toFixed(2)),
  }));

  return { points, linePath, xLabels, yTicks, parityY, chartW, chartH };
};

export const buildAgeTableSampleRows = (ageData = []) => {
  const sampleAges = [4, 5, 6, 7, 10, 14, 17, 18];
  return sampleAges.map((age) => {
    const row = ageRow(ageData, age);
    const gpi = row.gpi ?? 0;
    const status = getGpiStatus(gpi);
    return {
      age,
      boys: row.boys ?? 0,
      girls: row.girls ?? 0,
      total: row.total ?? 0,
      gpi: gpi.toFixed(3),
      status,
    };
  });
};

export const buildJourneyCards = (overview = {}, ageData = []) => {
  const retention = overview.secondary_retention_index ?? 0;
  const dropPct = Math.round((1 - retention) * 100);
  const total = overview.total_enrolment ?? 1;

  const early = sumAges(ageData, [4, 5, 6]);
  const primary = sumAges(ageData, [7, 8, 9, 10]);
  const secondary = sumAges(ageData, [14, 15, 16, 17, 18]);

  const earlyPct = ((early.total / total) * 100).toFixed(1);

  return [
    {
      className: "green",
      label: "Early Childhood · 4–6",
      value: fmt(early.total),
      sub: `${earlyPct}% of total enrolment. Strengthen early-age tracking and school readiness.`,
      barWidth: clamp((early.total / primary.total) * 100, 8, 100),
    },
    {
      className: "",
      label: "Primary · 7–10",
      value: fmt(primary.total),
      sub: "Peak enrolment stage. Use as baseline for retention comparison.",
      barWidth: 82,
    },
    {
      className: "orange",
      label: "Secondary · 14–18",
      value: fmt(secondary.total),
      sub: "Retention focus zone where girls participation starts weakening.",
      barWidth: clamp((secondary.total / primary.total) * 100, 8, 100),
    },
    {
      className: "red",
      label: "Retention Index",
      value: retention.toFixed(2),
      sub: `${dropPct}% drop-off from primary stage; requires block-level follow-up.`,
      barWidth: clamp(retention * 100, 8, 100),
    },
  ];
};

export const buildAgeGenderActions = (overview = {}, ageData = [], blockData = []) => {
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;
  const lowGpiBlocks = blockData.filter((b) => (b.gender_parity_index ?? b.gpi ?? 1) < 0.85).length;
  const adolescentRows = (ageData || []).filter((row) => {
    const age = parseInt(row.age, 10);
    return age >= 14 && age <= 18;
  });
  const adolescentTotal = adolescentRows.reduce((sum, row) => sum + (row.total ?? 0), 0);

  return [
    {
      title: "Protect girls' secondary transition",
      body: `Prioritize ages 14–18 (${fmt(adolescentTotal)} students in scope) for counselling and dropout prevention.`,
      iconClass: "pink",
    },
    {
      title: "Investigate Parity Dip",
      body:
        lowGpiBlocks > 0
          ? `Review ${lowGpiBlocks} block${lowGpiBlocks === 1 ? "" : "s"} with GPI below 0.85; district GPI is ${gpi}.`
          : `District GPI is ${gpi}; continue monitoring ages 17 and 18 for parity dips.`,
      iconClass: "green",
    },
    {
      title: "Track Retention Monthly",
      body: `Set monthly reviews while secondary retention is ${(retention * 100).toFixed(1)}% and re-enrolment cases remain open.`,
      iconClass: "orange",
    },
  ];
};

const blockSchools = (b) => b.total_schools ?? 0;
const blockAvg = (b) => b.avg_per_school ?? 0;
const blockGpi = (b) => b.gender_parity_index ?? 0;
const blockName = (b) => b.block_name ?? "—";
const schoolTotal = (s) => s.total_students ?? s.total_enrolment ?? 0;
const schoolBoys = (s) => s.boys ?? s.total_boys ?? 0;
const schoolGirls = (s) => s.girls ?? s.total_girls ?? 0;
const schoolGpi = (s) => s.gender_parity_index ?? 0;

export const getBlockStatusLabel = (block) => {
  const gpi = blockGpi(block);
  const avg = blockAvg(block);
  if (gpi >= 1.0) return "Above parity";
  if (gpi < 0.85) return "Critical parity";
  if (avg >= 600) return "High load";
  if (gpi < 0.9) return "Parity watch";
  return "Stable";
};

export const getSchoolReviewFlag = (school, rank = 99) => {
  const gpi = schoolGpi(school);
  if (gpi === 0 || gpi < 0.5) return "Single-gender review";
  if (gpi >= 1.0) return "Above parity";
  if (gpi >= 0.93) return "Stable";
  if (rank <= 2) return "Capacity watch";
  return "Parity watch";
};

export const buildGeoHero = (overview = {}, blockData = [], schoolSizeData = null) => {
  const blocks = [...blockData];
  const totalBlocks = blocks.length || (overview.total_blocks ?? 0);
  const median = schoolSizeData?.median_size ?? overview.avg_students_per_school ?? 0;
  const avg = schoolSizeData?.avg_size ?? overview.avg_students_per_school ?? 0;

  const bySchools = [...blocks].sort((a, b) => blockSchools(b) - blockSchools(a));
  const byAvg = [...blocks].sort((a, b) => blockAvg(b) - blockAvg(a));
  const byGpi = [...blocks].sort((a, b) => blockGpi(b) - blockGpi(a));

  const topBySchools = bySchools[0];
  const topByAvg = byAvg[0];
  const bestGpi = byGpi[0];
  const gpiOutliers = blocks.filter((b) => blockGpi(b) > 1.0).length;

  const highDensityNames = byAvg
    .slice(0, 4)
    .map(blockName)
    .filter(Boolean)
    .join(", ");

  const mapPins = bySchools.slice(0, 4).map((b, idx) => ({
    value: blockSchools(b),
    className: `p${idx + 1}`,
  }));

  return {
    mapPins,
    captionTitle: "Geographic enrolment is concentrated in a few high-density blocks",
    captionBody: highDensityNames
      ? `${highDensityNames} need capacity monitoring, while low-enrolment schools need consolidation and outreach review.`
      : "Review block concentration and low-enrolment schools for consolidation and outreach.",
    chips: [
      { label: `${totalBlocks} Blocks Ranked`, className: "" },
      { label: `Median School Size ${Math.round(median)}`, className: "orange" },
      { label: `Avg School Size ${Math.round(avg)}`, className: "" },
      { label: `${gpiOutliers} GPI Outliers Above Parity`, className: "" },
    ],
    metrics: [
      {
        label: "Schools Covered",
        value: fmt(overview.total_schools ?? schoolSizeData?.total_schools),
        sub: "Across selected district",
        warn: false,
      },
      {
        label: "Top Block Schools",
        value: fmt(topBySchools ? blockSchools(topBySchools) : 0),
        sub: topBySchools ? `${blockName(topBySchools)} has highest count` : "—",
        warn: false,
      },
      {
        label: "High-Density Block",
        value: fmt(topByAvg ? blockAvg(topByAvg) : 0),
        sub: topByAvg ? `${blockName(topByAvg)} avg/school` : "—",
        warn: true,
      },
      {
        label: "Best GPI Signal",
        value: bestGpi ? blockGpi(bestGpi).toFixed(3) : "—",
        sub: bestGpi ? `${blockName(bestGpi)} above parity` : "—",
        warn: false,
      },
    ],
  };
};

export const buildBlockRankCards = (blockData = [], limit = 8) =>
  blockData.slice(0, limit).map((block) => {
    const gpi = blockGpi(block);
    return {
      rank: block.rank,
      blockCode: block.block_code,
      name: blockName(block),
      schools: blockSchools(block),
      avg: blockAvg(block),
      gpi: gpi.toFixed(3),
      gpiClass: gpi >= 1.0 ? "" : "amber",
      status: getBlockStatusLabel(block),
    };
  });

export const buildGpiHbarChart = (blockData = [], limit = 7) => {
  const sorted = [...blockData].sort((a, b) => blockGpi(b) - blockGpi(a)).slice(0, limit);
  const maxGpi = Math.max(...sorted.map(blockGpi), 1);

  return sorted.map((block) => {
    const gpi = blockGpi(block);
    return {
      name: blockName(block),
      gpi: gpi.toFixed(3),
      widthPct: clamp((gpi / maxGpi) * 100, 12, 100),
      barClass: gpi >= 1.0 ? "green" : "",
    };
  });
};

export const buildSizeChartBars = (schoolSizeData = null) => {
  const bands = schoolSizeData?.distribution ?? [];
  const max = Math.max(...bands.map((b) => b.count ?? 0), 1);
  const maxHeight = 210;

  return bands.map((band) => ({
    band: (band.band ?? "").replace("-", "–"),
    count: band.count ?? 0,
    heightPx: Math.max(Math.round(((band.count ?? 0) / max) * maxHeight), 12),
  }));
};

export const buildGeoOperationalActions = (blockData = []) => {
  const byAvg = [...blockData].sort((a, b) => blockAvg(b) - blockAvg(a));
  const lowGpi = [...blockData]
    .filter((b) => blockGpi(b) < 0.9)
    .sort((a, b) => blockGpi(a) - blockGpi(b));
  const highGpi = blockData.filter((b) => blockGpi(b) > 1.0);

  const capacityBlocks = byAvg
    .slice(0, 3)
    .map(blockName)
    .join(", ");
  const parityBlocks = lowGpi
    .slice(0, 3)
    .map(blockName)
    .join(", ");
  const outlierBlocks = highGpi
    .slice(0, 2)
    .map(blockName)
    .join(", ");

  return [
    {
      title: "Capacity Pressure",
      body: capacityBlocks
        ? `${capacityBlocks} show high average students per school and should be checked for classroom and teacher adequacy.`
        : "Review blocks with high average students per school for classroom and teacher adequacy.",
      iconClass: "orange",
    },
    {
      title: "Girls Participation Watch",
      body: parityBlocks
        ? `${parityBlocks} should be reviewed for GPI below 0.90 and targeted outreach.`
        : "Review blocks with GPI below 0.90 for targeted outreach.",
      iconClass: "",
    },
    {
      title: "Outlier Validation",
      body: outlierBlocks
        ? `${outlierBlocks} show above-parity GPI. Validate data quality and identify transferable practices.`
        : "Validate above-parity GPI blocks and identify transferable practices.",
      iconClass: "green",
    },
  ];
};

export const buildTopSchoolRows = (topSchools = [], limit = 10) =>
  topSchools.slice(0, limit).map((school) => {
    const gpi = schoolGpi(school);
    const rank = school.rank ?? 0;
    return {
      rank,
      udiseCode: school.udise_code,
      schoolName: school.school_name ?? "—",
      blockName: school.block_name ?? "—",
      blockCode: school.block_code,
      boys: schoolBoys(school),
      girls: schoolGirls(school),
      total: schoolTotal(school),
      gpi: gpi.toFixed(3),
      gpiClass: gpi >= 1.0 ? "green" : "",
      reviewFlag: getSchoolReviewFlag(school, rank),
      showDash: schoolBoys(school) === 0 && schoolGirls(school) === 0,
    };
  });

export const mgmtEnrolment = (m = {}) => m.total_enrolment ?? m.total_students ?? 0;
export const mgmtGpi = (m = {}) => m.gender_parity_index ?? 0;
export const mgmtBoys = (m = {}) => m.total_boys ?? m.boys ?? 0;
export const mgmtGirls = (m = {}) => m.total_girls ?? m.girls ?? 0;

export const getMgmtStatusTag = (gpi = 0) => {
  if (gpi > 1.05) return { label: "Girls Lead", tagClass: "good" };
  if (gpi >= 0.95) return { label: "Balanced", tagClass: "good" };
  if (gpi >= 0.9) return { label: "Monitor", tagClass: "watch" };
  if (gpi < 0.5) return { label: "Validate", tagClass: "risk" };
  if (gpi < 0.8) return { label: "Low GPI", tagClass: "risk" };
  return { label: "Parity Watch", tagClass: "watch" };
};

const MGMT_SNAPSHOT_THEMES = [
  { iconClass: "", barClass: "", barColor: "var(--aew-primary)" },
  { iconClass: "green", barClass: "green", barColor: "var(--aew-accent)" },
  { iconClass: "orange", barClass: "orange", barColor: "var(--aew-secondary)" },
  { iconClass: "purple", barClass: "purple", barColor: "var(--aew-purple)" },
];

const MGMT_HBAR_COLORS = ["", "green", "orange", "purple", "pink"];

export const buildPolicyDonutGauge = (managementData = [], overview = {}) => {
  const total =
    overview.total_enrolment ?? managementData.reduce((s, m) => s + mgmtEnrolment(m), 0);
  const sorted = [...managementData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a));
  const top = sorted.slice(0, 4);
  const topRow = top[0] ?? {};
  const topShare = total > 0 ? (mgmtEnrolment(topRow) / total) * 100 : 0;

  let cursor = 0;
  const stops = top.map((row, idx) => {
    const share = total > 0 ? (mgmtEnrolment(row) / total) * 100 : 0;
    const start = cursor;
    cursor += share;
    return `${MGMT_COLORS[idx % MGMT_COLORS.length]} ${start}% ${cursor}%`;
  });

  return {
    topSharePct: topShare.toFixed(1),
    topName: topRow.management_name ?? "—",
    style: {
      background: stops.length
        ? `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${stops.join(", ")})`
        : "radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#e2e8f0 0 100%)",
    },
  };
};

export const buildPolicyHero = (managementData = [], overview = {}, dataQuality = null) => {
  const donut = buildPolicyDonutGauge(managementData, overview);
  const sorted = [...managementData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a));
  const top = sorted[0];
  const balanced = sorted.find((m) => mgmtGpi(m) >= 0.95 && mgmtEnrolment(m) > 50000);
  const lowGpiCount = sorted.filter((m) => mgmtGpi(m) < 0.85).length;
  const completeness =
    typeof dataQuality?.completeness_score === "number" ? dataQuality.completeness_score : null;

  const chips = [
    { label: "Concentration Watch", className: "amber" },
    completeness != null && completeness >= 99.9
      ? { label: `${Math.round(completeness)}% Complete Data`, className: "green" }
      : completeness != null
        ? { label: `${Math.round(completeness)}% Complete Data`, className: "" }
        : { label: "Completeness Data Pending", className: "" },
    { label: `${managementData.length} Management Types`, className: "" },
  ];

  const topName = top?.management_name ?? "largest management group";
  const benchmarkName = balanced?.management_name ?? "balanced management types";

  return {
    donut,
    headline: "Management Mix Requires Targeted Policy Monitoring",
    body: `${topName} forms the largest enrolment base${balanced ? `, while ${benchmarkName} shows strong parity` : ""}. ${lowGpiCount > 0 ? "Low-GPI management categories need focused follow-up and validation." : "Review management-type parity during district planning cycles."}`,
    chips,
    kpis: [
      {
        label: "Total Records",
        value: fmt(dataQuality?.total_records ?? 0),
        sub: "Validated entries",
        cardClass: "blue",
      },
      {
        label: "Unique Schools",
        value: fmt(dataQuality?.total_schools ?? overview.total_schools ?? 0),
        sub: "Policy coverage base",
        cardClass: "blue",
      },
      {
        label: "Completeness",
        value: pct(completeness, 0),
        sub: "Consistent dataset",
        cardClass: "",
      },
      {
        label: "Zero Enrolment",
        value: fmt(dataQuality?.zero_enrolment_records ?? 0),
        sub: "No empty records",
        cardClass: "amber",
      },
    ],
  };
};

export const buildMgmtSnapshotCards = (managementData = [], overview = {}) => {
  const total =
    overview.total_enrolment ?? managementData.reduce((s, m) => s + mgmtEnrolment(m), 0);
  const sorted = [...managementData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a));

  return sorted.slice(0, 4).map((row, idx) => {
    const theme = MGMT_SNAPSHOT_THEMES[idx] ?? MGMT_SNAPSHOT_THEMES[0];
    const enrol = mgmtEnrolment(row);
    const share = total > 0 ? (enrol / total) * 100 : 0;
    const gpi = mgmtGpi(row);

    return {
      key: row.management_code ?? idx,
      name: row.management_name ?? "—",
      students: formatLakh(enrol),
      sharePct: share,
      schools: fmt(row.total_schools ?? 0),
      gpi: gpi.toFixed(3),
      iconClass: theme.iconClass,
      barClass: theme.barClass,
      barColor: theme.barColor,
      dotClass: gpi < 0.9 ? "amber" : "",
    };
  });
};

export const buildMgmtHbarChart = (managementData = [], limit = 5) => {
  const sorted = [...managementData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a)).slice(0, limit);
  const max = Math.max(...sorted.map(mgmtEnrolment), 1);

  return sorted.map((row, idx) => {
    const enrol = mgmtEnrolment(row);
    const name = row.management_name ?? "—";
    return {
      key: row.management_code ?? idx,
      label: name,
      value: formatLakh(enrol),
      widthPct: clamp((enrol / max) * 100, 2, 100),
      barClass: MGMT_HBAR_COLORS[idx] ?? "",
    };
  });
};

export const buildCategoryBars = (categoryData = [], limit = 7) => {
  const sorted = [...categoryData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a)).slice(0, limit);
  const max = Math.max(...sorted.map(mgmtEnrolment), 1);

  return sorted.map((row, idx) => ({
    key: row.category_code ?? idx,
    name: row.category_name ?? "—",
    value: formatCompactK(mgmtEnrolment(row)),
    widthPct: clamp((mgmtEnrolment(row) / max) * 100, 5, 100),
  }));
};

export const buildQualityScorecard = (dataQuality = null) => {
  const consistent = dataQuality?.data_consistent ?? true;
  return [
    {
      label: "Total Records",
      value: fmt(dataQuality?.total_records ?? 0),
      sub: "All records loaded",
    },
    {
      label: "Unique Schools",
      value: fmt(dataQuality?.total_schools ?? 0),
      sub: "School base verified",
    },
    {
      label: "Data Consistency",
      value: consistent ? "Good" : "Review",
      sub: consistent ? "Consistent" : "Mismatch found",
    },
    {
      label: "Completeness",
      value: pct(dataQuality?.completeness_score ?? 0, 0),
      sub: "No missing coverage",
    },
    {
      label: "Zero Enrolment",
      value: fmt(dataQuality?.zero_enrolment_records ?? 0),
      sub: "No zero records",
    },
  ];
};

export const buildMgmtTableRows = (managementData = []) =>
  [...managementData]
    .sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a))
    .map((row) => {
      const gpi = mgmtGpi(row);
      const status = getMgmtStatusTag(gpi);
      return {
        key: row.management_code ?? row.management_name,
        name: row.management_name ?? "—",
        schools: fmt(row.total_schools ?? 0),
        boys: mgmtBoys(row),
        girls: mgmtGirls(row),
        total: mgmtEnrolment(row),
        gpi: gpi.toFixed(3),
        statusLabel: status.label,
        statusClass: status.tagClass,
      };
    });

export const buildPolicyActions = (managementData = []) => {
  const sorted = [...managementData].sort((a, b) => mgmtEnrolment(b) - mgmtEnrolment(a));
  const largest = sorted[0];
  const benchmarks = sorted.filter((m) => mgmtGpi(m) >= 0.95).slice(0, 2);
  const extremes = sorted
    .filter((m) => mgmtGpi(m) < 0.5 || mgmtGpi(m) > 1.2)
    .slice(0, 3);

  const largestName = largest?.management_name ?? "largest management group";
  const largestGpi = mgmtGpi(largest).toFixed(3);
  const benchmarkNames = benchmarks.map((m) => m.management_name).join(" and ") || "high-parity schools";
  const extremeNames =
    extremes.map((m) => m.management_name).join(", ") || "selected management types";

  return {
    items: [
      {
        title: "Review Private Unaided Gender Parity",
        body: `Largest enrolment group has GPI ${largestGpi}. Track girls participation, transition and retention by block.`,
        iconClass: "",
      },
      {
        title: "Use Local Body as Parity Benchmark",
        body: `${benchmarkNames} show strong GPI. Compare practices with low-parity management types.`,
        iconClass: "green",
      },
      {
        title: "Validate Extreme GPI Categories",
        body: `${extremeNames} show very low GPI values; confirm source data and admissions pattern.`,
        iconClass: "orange",
      },
    ],
    footer:
      "Run a management-type parity review every month and prioritize interventions where enrolment is high and GPI is below 0.90.",
  };
};

const parseAgeKey = (age) => {
  if (typeof age === "number" && Number.isFinite(age)) return age;
  const parsed = parseInt(String(age ?? ""), 10);
  return Number.isFinite(parsed) ? parsed : -1;
};

export const sumAgeRange = (ageData = [], min, max) =>
  ageData.reduce((sum, row) => {
    const age = parseAgeKey(row.age ?? row._id);
    if (age >= min && age <= max) return sum + (row.total ?? 0);
    return sum;
  }, 0);

export const buildAgeInsightsHero = (overview = {}) => {
  const peakAge = overview.peak_enrolment_age ?? "—";
  const retention = overview.secondary_retention_index ?? 0;

  return {
    summary: `Enrolment is strong at the primary level with a peak at age ${peakAge}. Priority focus is secondary retention and the transition window around ages 13–14.`,
    metrics: [
      {
        label: "Total Enrolment",
        value: fmt(overview.total_enrolment),
        sub: `Students across ${fmt(overview.total_schools)} schools`,
        iconClass: "",
      },
      {
        label: "Gender Parity Index (gpi)",
        value: (overview.gender_parity_index ?? 0).toFixed(3),
        sub: "Girls per boys",
        iconClass: "green",
      },
      {
        label: "Peak Enrolment Age",
        value: `${peakAge} yrs`,
        sub: `${fmt(overview.peak_enrolment_count)} students`,
        iconClass: "orange",
      },
      {
        label: "Secondary Retention Index",
        value: retention.toFixed(2),
        sub: "Ages 14–18 retention",
        iconClass: "purple",
      },
    ],
  };
};

export const buildAgeInsightsKeyInsights = (overview = {}, ageData = []) => {
  const total = overview.total_enrolment ?? 0;
  const boys = overview.total_boys ?? overview.boys ?? 0;
  const girls = overview.total_girls ?? overview.girls ?? 0;
  const boysPct = total > 0 ? ((boys / total) * 100).toFixed(1) : "0.0";
  const girlsPct = (overview.girls_pct ?? 0).toFixed(1);
  const primary = sumAgeRange(ageData, 6, 13) || overview.primary_total || 0;
  const secondary = sumAgeRange(ageData, 14, 18) || overview.secondary_total || 0;
  const gpi = (overview.gender_parity_index ?? 0).toFixed(3);
  const retention = (overview.secondary_retention_index ?? 0).toFixed(2);

  return [
    {
      segments: [
        { text: "Total enrolment: " },
        { text: fmt(total), bold: true },
        { text: " students across " },
        { text: fmt(overview.total_schools), bold: true },
        { text: " schools." },
      ],
    },
    {
      segments: [
        { text: "Gender distribution: Boys " },
        { text: fmt(boys), bold: true },
        { text: ` (${boysPct}%), Girls ` },
        { text: fmt(girls), bold: true },
        { text: ` (${girlsPct}%).` },
      ],
    },
    {
      segments: [
        { text: "Gender Parity Index (GPI): " },
        { text: gpi, bold: true },
        { text: "." },
      ],
    },
    {
      segments: [
        { text: "Early age (4–6): " },
        { text: fmt(overview.early_age_total), bold: true },
        { text: ` students (${(overview.early_age_pct ?? 0).toFixed(1)}%).` },
      ],
    },
    {
      segments: [
        { text: "Primary (6–13): " },
        { text: fmt(primary), bold: true },
        { text: " students." },
      ],
    },
    {
      segments: [
        { text: "Secondary (14–18): " },
        { text: fmt(secondary), bold: true },
        { text: " students." },
      ],
    },
    {
      segments: [
        { text: "Peak enrolment age: " },
        { text: `${overview.peak_enrolment_age ?? "—"} years`, bold: true },
        { text: " with " },
        { text: fmt(overview.peak_enrolment_count), bold: true },
        { text: " students." },
      ],
    },
    {
      segments: [
        { text: "Secondary retention index: " },
        { text: retention, bold: true },
        { text: "." },
      ],
    },
  ];
};

export const buildAgeInsightsRootCauses = (overview = {}) => {
  const gpi = overview.gender_parity_index ?? 0;
  const retention = overview.secondary_retention_index ?? 0;

  return [
    {
      text:
        gpi < 0.97
          ? "Girls lag behind boys in enrolment parity."
          : "Gender parity remains near balance but needs block-level monitoring.",
      icon: "clock",
    },
    {
      text: "Drop-off observed after primary stage.",
      icon: "user",
    },
    {
      text: "Retention pressure in secondary stage (14–18).",
      icon: "chart",
    },
    {
      text:
        retention < 0.8
          ? "Transition bottleneck at ages 13–14."
          : "Transition support needed around ages 13–14.",
      icon: "stairs",
    },
  ];
};

export const buildAgeInsightsRecommendations = (overview = {}, ageData = [], blockData = []) => {
  const retention = overview.secondary_retention_index ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const lowGpiBlocks = blockData.filter((b) => (b.gender_parity_index ?? b.gpi ?? 1) < 0.9).length;
  const age1314 = (ageData || []).filter((row) => [13, 14].includes(parseInt(row.age, 10)));
  const age1316Total = (ageData || [])
    .filter((row) => {
      const age = parseInt(row.age, 10);
      return age >= 13 && age <= 16;
    })
    .reduce((sum, row) => sum + (row.total ?? 0), 0);

  return [
    {
      text: `Strengthen transition support for ${fmt(age1314.reduce((s, r) => s + (r.total ?? 0), 0))} students at ages 13–14.`,
      icon: "trend",
    },
    {
      text:
        gpi < 0.95
          ? `Run focused retention programs for adolescent girls while GPI is ${gpi}.`
          : `Maintain girls' retention programs; district GPI is ${gpi}.`,
      icon: "user-plus",
    },
    {
      text:
        retention < 0.8
          ? `Monitor drop-off cohorts; secondary retention is ${(retention * 100).toFixed(1)}%.`
          : "Monitor drop-off cohorts even with stable secondary retention.",
      icon: "refresh",
    },
    {
      text:
        lowGpiBlocks > 0
          ? `Target ages 13–16 in ${lowGpiBlocks} low-GPI block${lowGpiBlocks === 1 ? "" : "s"} (${fmt(age1316Total)} students).`
          : `Target interventions across ${fmt(age1316Total)} students in ages 13–16.`,
      icon: "plus",
    },
  ];
};

export const buildAgeInsightsPriorityActions = (overview = {}, blockData = []) => {
  const retention = overview.secondary_retention_index ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const lowGpiBlocks = [...blockData]
    .filter((b) => (b.gpi ?? b.gender_parity_index ?? 1) < 0.9)
    .length;

  return [
    {
      num: 1,
      text: "Launch district-wide retention campaign for ages 14–18.",
      priority: retention < 0.75 ? "high" : "medium",
    },
    {
      num: 2,
      text:
        lowGpiBlocks > 0
          ? `Conduct block-level review of gender parity gaps (${lowGpiBlocks} blocks below 0.90 GPI).`
          : "Conduct block-level review of gender parity gaps.",
      priority: gpi < 0.95 ? "medium" : "medium",
    },
    {
      num: 3,
      text: "Strengthen school outreach for transition from primary to secondary.",
      priority: "medium",
    },
  ];
};

export const buildAgeInsightsNarrative = (overview = {}) => {
  const gpi = (overview.gender_parity_index ?? 0).toFixed(3);
  const peakAge = overview.peak_enrolment_age ?? 12;

  return [
    {
      title: "Strong Foundation at Primary Level",
      body: `High enrolment in primary ages and a peak at age ${peakAge} indicate strong access and demand.`,
      icon: "chart",
    },
    {
      title: "Critical Window for Retention",
      body: "Drop-off after primary and retention pressure in secondary require focused action.",
      icon: "shield",
    },
    {
      title: "Equity Opportunity",
      body: `GPI of ${gpi} shows a gender gap that can be narrowed with targeted support for girls.`,
      icon: "bars",
    },
  ];
};

export const buildAgeInsightsWatchlist = (overview = {}, blockData = []) => {
  const retention = overview.secondary_retention_index ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const lowestGpiBlock = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (a.gender_parity_index ?? a.gpi ?? 1) - (b.gender_parity_index ?? b.gpi ?? 1))[0];

  return [
    {
      text: `Monitor ages 13–16 while secondary retention is ${(retention * 100).toFixed(1)}%`,
      iconClass: "",
    },
    {
      text: lowestGpiBlock
        ? `Track GPI in ${lowestGpiBlock.block_name} (${lowestGpiBlock.gender_parity_index ?? lowestGpiBlock.gpi ?? gpi})`
        : `Track GPI across ${blockData.length} blocks (district GPI ${gpi})`,
      iconClass: "pink",
    },
    {
      text: `Review secondary retention quarterly (current index ${(retention * 100).toFixed(1)}%)`,
      iconClass: "green",
    },
  ];
};

export const buildAgeExecutiveMarkdown = (overview = {}, blockData = []) => {
  const insights = buildExecutiveInsights?.(overview) || [];
  const queue = buildExecutiveActionQueue?.(overview) || [];

  return [
    "## Insights",
    `- ${buildHeroNarrative(overview)}`,
    ...(insights.length ? insights.map((i) => `- ${i.title}: ${i.body}`) : []),
    "",
    "## Priority Action Items",
    `- ${buildPrimaryExecutiveAction(overview)}`,
    ...(queue.length ? queue.map((a) => `- ${a.title}: ${a.body}`) : []),
  ].join("\n");
};

export const buildAgeInsightsMarkdown = (overview = {}, ageData = [], blockData = []) => {
  const hero = buildAgeInsightsHero(overview);
  const keyInsights = buildAgeInsightsKeyInsights(overview, ageData);
  const rootCauses = buildAgeInsightsRootCauses(overview);
  const recommendations = buildAgeInsightsRecommendations(overview, ageData, blockData);
  const actions = buildAgeInsightsPriorityActions(overview, blockData);

  const segmentLine = (item) =>
    item.segments?.map((s) => (s.bold ? `**${s.text}**` : s.text)).join("") ?? "";

  return [
    "## Insights",
    hero.summary,
    ...keyInsights.map(segmentLine),
    "",
    "## Root Cause Signals",
    ...rootCauses.map((r) => `- ${r.text}`),
    "",
    "## Recommendations",
    ...recommendations.map((r) => `- ${r.text}`),
    "",
    "## Priority Action Items",
    ...actions.map((a) => `- ${a.text}`),
  ].join("\n");
};
